import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clapperboard, MessageSquare, Send, Trash2, UploadCloud, Video, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { localeUI } from '../lib/format';
import { fmtBytes, type AtrVideoDTO } from '../lib/atrVideos';
import { useAtrUpload } from '../lib/useAtrUpload';
import { AtrVideoPlayer } from './AtrVideoPlayer';
import type { VideoDelClaim } from './ClaimVideosBox';

/**
 * BOTONES "ATR videos" / "Informative videos" DE LA TARJETA DEL CLAIM (pedido del dueño 2026-09-29):
 * a la DERECHA de "Open photos", en la misma fila. Gris si no hay videos; con color si hay.
 *
 * Un click abre el modal de ESE tipo con:
 *  - los videos EMBEBIDOS. La fuente es la MISMA que el resto de la app: el ATR sale de
 *    `/api/claims/:id/atr-videos` (el cuadro ATR: archivo subido por el inspector, link pegado en la
 *    cita o en Files) y los informativos de `/api/claims/:id/videos`. Lo que sube el inspector es lo
 *    que se ve acá, sin copia aparte.
 *  - ARRASTRAR Y SOLTAR para subir desde la oficina (mismo motor multipart a R2 que el teléfono,
 *    pegado al claim sin cita). Al terminar el backend le avisa a todo el claim team.
 *  - COMENTARIOS por video (mig 0462), llave `video_ref` = v<id> | a<id> | f<id>.
 *
 * `?videos=atr|info` en la URL abre el modal solo: es a donde apunta la notificación del claim team.
 */
type Kind = 'atr' | 'info';
type AtrEntry = { id: string; kind: 'appt' | 'file' | 'video'; videoUrl: string | null; at: string | null; inspectorName: string | null; label?: string | null; status: 'uploaded' | 'pending'; video?: AtrVideoDTO | null };
type Item = { ref: string; url: string; title: string; at: string | null; by: string | null; codec: string | null; size: number | null; fromOffice: boolean };
type Comentario = { id: string; videoRef: string; body: string; author: string | null; createdAt: string; mine: boolean };

const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

function useVideosParaBotones(claimId: string) {
  const [atr, setAtr] = useState<Item[] | null>(null);
  const [info, setInfo] = useState<Item[] | null>(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    try {
      const [a, v] = await Promise.all([
        api.get<{ videos: AtrEntry[] }>(`/api/claims/${claimId}/atr-videos`),
        api.get<{ videos: VideoDelClaim[] }>(`/api/claims/${claimId}/videos`),
      ]);
      setAtr(a.videos.filter((x) => x.status === 'uploaded' && x.videoUrl).map((x) => ({
        ref: x.id, url: x.videoUrl!, title: x.label || x.video?.title || x.video?.fileName || 'ATR video',
        at: x.at, by: x.inspectorName, codec: x.video?.codec ?? null, size: x.video?.sizeBytes ?? null,
        fromOffice: Boolean(x.video && !x.video.appointmentId),
      })));
      setInfo(v.videos.filter((x) => x.kind === 'info').map((x) => ({
        ref: `v${x.id}`, url: x.mediaUrl, title: x.title || x.fileName, at: x.readyAt ?? x.createdAt,
        by: x.uploadedBy, codec: x.codec, size: x.sizeBytes, fromOffice: !x.appointmentId,
      })));
      setError(false);
    } catch { setError(true); }
  }, [claimId]);
  useEffect(() => { setAtr(null); setInfo(null); void load(); }, [load]);
  return { atr, info, error, load };
}

export function ClaimVideoButtons({ claimId, autoOpen = true }: {
  claimId: string;
  /** Abrir solo con `?videos=` (la notificación apunta a /claims/:id). El drawer pasa false: vive sobre listas y monta dos cabeceras. */
  autoOpen?: boolean;
}) {
  const { t } = useI18n();
  const { atr, info, error, load } = useVideosParaBotones(claimId);
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState<Kind | null>(null);
  // La notificación del claim team trae ?videos=atr|info: abre el modal y limpia el parámetro.
  useEffect(() => {
    if (!autoOpen) return;
    const q = sp.get('videos');
    if (q === 'atr' || q === 'info') {
      setOpen(q);
      const n = new URLSearchParams(sp); n.delete('videos'); setSp(n, { replace: true });
    }
  }, [sp, setSp, autoOpen]);

  const boton = (kind: Kind, items: Item[] | null) => {
    const n = items?.length ?? 0;
    const hay = n > 0;
    const tono = kind === 'atr' ? { bg: 'var(--green-soft)', fg: 'var(--green)' } : { bg: 'var(--info-soft)', fg: 'var(--info)' };
    const Icon = kind === 'atr' ? Clapperboard : Video;
    return (
      <button type="button" className="btn sm" onClick={() => setOpen(kind)}
        title={hay ? undefined : t('cvb.noneHint')}
        style={hay
          ? { background: tono.bg, color: tono.fg, border: `1px solid ${tono.fg}`, fontWeight: 600 }
          : { background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--line)', fontWeight: 500 }}>
        <Icon size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t(kind === 'atr' ? 'cvb.atr' : 'cvb.info')}{hay ? ` (${n})` : ''}
      </button>
    );
  };

  return (
    <>
      {boton('atr', atr)}
      {boton('info', info)}
      {open && (
        <VideosModal claimId={claimId} kind={open} items={open === 'atr' ? atr : info} error={error}
          onReload={load} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

function VideosModal({ claimId, kind, items, error, onReload, onClose }: {
  claimId: string; kind: Kind; items: Item[] | null; error: boolean; onReload: () => Promise<void>; onClose: () => void;
}) {
  const { t } = useI18n();
  const [comentarios, setComentarios] = useState<Comentario[]>([]);
  const cargarComentarios = useCallback(() => api.get<{ comments: Comentario[] }>(`/api/claims/${claimId}/video-comments`)
    .then((r) => setComentarios(r.comments)).catch(() => {}), [claimId]);
  useEffect(() => { void cargarComentarios(); }, [cargarComentarios]);

  const up = useAtrUpload(() => { void onReload(); }, t);
  const [caption, setCaption] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [sobre, setSobre] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Esc cierra (salvo que haya una subida en curso: cerrar no la corta, pero se pierde de vista).
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !up.enCurso) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, up.enCurso]);

  function subir(files: FileList | File[] | null) {
    const lista = files ? Array.from(files) : [];
    if (!lista.length) return;
    if (up.enCurso) { setAviso(t('cvb.busy')); return; }
    const f = lista[0]!;
    if (!up.esVideo(f)) { setAviso(t('cvb.notVideo')); return; }
    setAviso(lista.length > 1 ? t('cvb.oneAtATime') : null);
    up.empezar(`claim-${claimId}`, f, {
      kind, caption: kind === 'info' ? (caption.trim() || null) : null,
      endpoints: { create: `/api/claims/${claimId}/video-upload`, base: '/api/claim-videos' },
    });
    setCaption('');
  }

  const s = up.subida;
  return (
    <div className="ash-modal-backdrop" onClick={() => { if (!up.enCurso) onClose(); }}>
      <div className="ash-modal cvb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ash-modal-head">
          <b>{kind === 'atr' ? <Clapperboard size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> : <Video size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />} {t(kind === 'atr' ? 'cvb.atr' : 'cvb.info')}{items?.length ? ` (${items.length})` : ''}</b>
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label={t('common.close')}><X size={15} strokeWidth={1.75} /></button>
        </div>

        {/* ARRASTRAR Y SOLTAR (o click): sube directo a R2 por partes, igual que el teléfono. */}
        {kind === 'info' && !s && (
          <input className="input sm" style={{ width: '100%', marginBottom: 6 }} value={caption} maxLength={140}
            onChange={(e) => setCaption(e.target.value)} placeholder={t('cvb.captionPh')} />
        )}
        {s ? (
          <div className={`cvb-drop busy${s.p.phase === 'failed' ? ' failed' : ''}`}>
            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.file.name}</div>
            <div className="cvb-bar"><span style={{ width: `${s.p.pct}%` }} /></div>
            <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{up.estado(s.p)}</div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
              {s.p.phase === 'failed' && <button type="button" className="btn sm" onClick={up.reintentar}>{t('cvb.retry')}</button>}
              {s.p.phase !== 'done' && <button type="button" className="btn ghost sm" onClick={() => void up.cancelar()}>{t('common.cancel')}</button>}
            </div>
          </div>
        ) : (
          <div
            className={`cvb-drop${sobre ? ' over' : ''}`} role="button" tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); setSobre(true); }}
            onDragLeave={() => setSobre(false)}
            onDrop={(e) => { e.preventDefault(); setSobre(false); subir(e.dataTransfer.files); }}>
            <UploadCloud size={22} strokeWidth={1.5} />
            <div style={{ fontWeight: 600 }}>{t('cvb.drop')}</div>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cvb.dropHint')}</div>
            <input ref={inputRef} type="file" accept="video/*,.mov,.mp4,.m4v,.webm,.mkv,.3gp" hidden
              onChange={(e) => { subir(e.target.files); e.target.value = ''; }} />
          </div>
        )}
        {aviso && <div style={{ color: 'var(--orange)', fontSize: 'var(--fs-xs)', marginTop: 4 }}>{aviso}</div>}

        <div style={{ display: 'grid', gap: 14, marginTop: 12 }}>
          {error && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{t('cvb.loadFailed')}</div>}
          {items === null && !error && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('common.loadingDots')}</div>}
          {items?.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('cvb.empty')}</div>}
          {items?.map((v) => (
            <VideoConComentarios key={v.ref} claimId={claimId} v={v}
              comentarios={comentarios.filter((c) => c.videoRef === v.ref)} onChanged={cargarComentarios} />
          ))}
        </div>
      </div>
    </div>
  );
}

function VideoConComentarios({ claimId, v, comentarios, onChanged }: { claimId: string; v: Item; comentarios: Comentario[]; onChanged: () => Promise<void> }) {
  const { t } = useI18n();
  const [texto, setTexto] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function publicar() {
    if (!texto.trim()) return;
    setBusy(true); setErr(null);
    try { await api.post(`/api/claims/${claimId}/video-comments`, { videoRef: v.ref, body: texto.trim() }); setTexto(''); await onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(false); }
  }
  async function borrar(id: string) {
    if (!window.confirm(t('cvb.deleteConfirm'))) return;
    try { await api.del(`/api/claims/${claimId}/video-comments/${id}`); await onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
  }

  const meta = [fecha(v.at), v.by ? t('cvb.by', { name: v.by }) : null, fmtBytes(v.size) || null, v.fromOffice ? t('cvb.fromOffice') : null].filter(Boolean).join(' · ');
  return (
    <div className="cvb-item">
      <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', overflowWrap: 'anywhere' }}>{v.title}</div>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6 }}>{meta}</div>
      <AtrVideoPlayer url={v.url} codec={v.codec} />
      <div className="cvb-comments">
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
          <MessageSquare size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cvb.comments')}{comentarios.length ? ` (${comentarios.length})` : ''}
        </div>
        {comentarios.map((c) => (
          <div key={c.id} className="cvb-comment">
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
              <b style={{ fontSize: 'var(--fs-xs)' }}>{c.author ?? '—'}</b>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fecha(c.createdAt)}</span>
              <span style={{ flex: 1 }} />
              {c.mine && (
                <button type="button" className="btn ghost sm" style={{ padding: '0 6px' }} title={t('cvb.deleteComment')} aria-label={t('cvb.deleteComment')} onClick={() => void borrar(c.id)}>
                  <Trash2 size={12} strokeWidth={1.75} />
                </button>
              )}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--fs-xs)', overflowWrap: 'anywhere' }}>{c.body}</div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <textarea className="input sm" rows={1} style={{ flex: 1, resize: 'vertical', minHeight: 32 }} value={texto} maxLength={4000}
            placeholder={t('cvb.commentPh')} onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void publicar(); } }} />
          <button type="button" className="btn sm" disabled={busy || !texto.trim()} onClick={() => void publicar()}>
            <Send size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cvb.post')}
          </button>
        </div>
        {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>}
      </div>
    </div>
  );
}
